import React, { FC } from 'react';

import TestCard, { QuizColorsMode } from '~/blocks/TestCard/TestCard';
import QuestionTitle from '~/components/QuestionQuiz/QuestionTitle';
import createCorrectAnswersByIdMap from '~/components/QuestionQuiz/createCorrectAnswersByIdMap';
import CarouselQuestion from '~/components/Test/CarouselQuestion/CarouselQuestion';
import ChatQuestion from '~/components/Test/ChatQuestion';
import OrderingQuestion from '~/components/Test/OrderingQuestion/OrderingQuestion';
import RadioQuestion from '~/components/Test/RadioQuestion';
import TypeResponseQuestion from '~/components/Test/TypeResponseQuestion';
import { QUESTION_TYPES } from '~/pages/const';
import { Maybe, QuizQuestionAnswer, QuizQuestionRadioType } from '~/types';

interface IQuestion {
  type: string;
  viewType?: Maybe<QuizQuestionRadioType>;
  title?: Maybe<string>;
  image?: Maybe<string>;
  answers: Maybe<QuizQuestionAnswer>[];
  selectedAnswersIds: string[];
  isCorrectByIdMap: ReturnType<typeof createCorrectAnswersByIdMap>;
  submitted: boolean;
  onOrderAnswers: (options: string[]) => void;
  onSelectSingleAnswer: (id: QuizQuestionAnswer['id']) => void;
  continueLearning?: () => void;
  openExplainer?: () => void;
  className?: string;
  colorsMode?: QuizColorsMode;
}

const Question: FC<IQuestion> = ({
  type,
  viewType,
  title,
  image,
  answers,
  selectedAnswersIds,
  isCorrectByIdMap,
  submitted,
  onOrderAnswers,
  onSelectSingleAnswer,
  className,
  colorsMode,
  openExplainer,
  continueLearning,
}) => {
  const questionTitleLayout = <QuestionTitle title={title} colorsMode={colorsMode} />;
  const correctIds = Object.keys(isCorrectByIdMap);
  const isEveryCorrectSelected = correctIds.every((id) => selectedAnswersIds.includes(id));

  if (type === QUESTION_TYPES.ordered) {
    return (
      <TestCard className={className} title={questionTitleLayout} image={image}>
        <OrderingQuestion
          handleOrderUpdate={onOrderAnswers}
          answerOptions={answers}
          order={selectedAnswersIds}
          isSmall
          disabled={submitted}
          displayResult={submitted}
          isCorrectByIdMap={isCorrectByIdMap}
          colorsMode={colorsMode}
        />
      </TestCard>
    );
  }

  if (type === QUESTION_TYPES.radio) {
    if (viewType === QuizQuestionRadioType.Chat) {
      return (
        <TestCard className={className}>
          <ChatQuestion
            title={title}
            onSelect={onSelectSingleAnswer}
            isCorrectByIdMap={isCorrectByIdMap}
            selectedAnswersIds={selectedAnswersIds}
            submitted={submitted}
            answers={answers}
            isCorrect={isEveryCorrectSelected}
            continueLearning={continueLearning}
            openExplainer={openExplainer}
          />
        </TestCard>
      );
    }

    if (viewType === QuizQuestionRadioType.EnterAnswer) {
      return (
        <TestCard className={className} title={questionTitleLayout} image={image}>
          <TypeResponseQuestion
            answers={answers}
            onSelect={onSelectSingleAnswer}
            isCorrectByIdMap={isCorrectByIdMap}
            selectedAnswersIds={selectedAnswersIds}
            submitted={submitted}
            isCorrect={isEveryCorrectSelected}
          />
        </TestCard>
      );
    }

    if (viewType === QuizQuestionRadioType.Carousel) {
      return (
        <TestCard className={className} title={questionTitleLayout}>
          <CarouselQuestion
            answers={answers}
            onSelect={onSelectSingleAnswer}
            submitted={submitted}
            isCorrect={isEveryCorrectSelected}
            isCorrectByIdMap={isCorrectByIdMap}
            selectedAnswersIds={selectedAnswersIds}
          />
        </TestCard>
      );
    }
  }

  return (
    <TestCard className={className} title={questionTitleLayout} image={image}>
      <RadioQuestion
        onSelect={onSelectSingleAnswer}
        isCorrectByIdMap={isCorrectByIdMap}
        selectedAnswersIds={selectedAnswersIds}
        submitted={submitted}
        answers={answers}
        colorsMode={colorsMode}
      />
    </TestCard>
  );
};

export default Question;
